import { Box, IconButton, Stack, Tooltip, Card, Typography } from "@mui/material";
import { FaCartShopping, FaMagnifyingGlass, FaMoneyBillWave } from "react-icons/fa6";
import { Link } from "react-router-dom";
import ListingPage from '../../components/DynamicListingPage';
import { useQuery } from '@tanstack/react-query';
import { formatCurrency } from '../../utils/functions';
import { useCatalog } from '../../providers/CatalogProvider';
import { useState } from "react";
import { getFinancialReports } from "../../helpers/http_financial_report_requests";
import { usePermissions } from "../../providers/PermissionsProvider";
import { getCustomers } from "../../helpers/http_customers_requests";

export default function Financial() {

    const { verifyPermission } = usePermissions();
    const { metalList } = useCatalog();

    // Estado para filtros
    const [filterValues, setFilterValues] = useState({});

    const handleFilterChange = (field, value) => {
        setFilterValues(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const customersQuery = useQuery({ queryKey: ['getCustomers'], queryFn: getCustomers, staleTime: 1000 * 60 });

    const customers = customersQuery?.data?.data?.customers?.map(item => ({ value: item.id, label: item.complete_name })) || [];

    const { data, isFetching } = useQuery({
        queryKey: ['getFinancialReports', filterValues],
        queryFn: () => getFinancialReports(filterValues),
        staleTime: 1000 * 5
    });

    const { data: dataList } = data?.data ?? {};

    const columns = [
        { field: 'id', headerName: 'ID' },
        { field: 'image', headerName: 'Foto' },
        {
            field: 'category_name',
            headerName: 'Categoria',
        },
        {
            field: 'created_by',
            headerName: 'Cadastrado',
        },
        {
            field: 'customer_name',
            headerName: 'Cliente',
        },
        {
            field: 'selected_metal',
            headerName: 'Material',
        },
        {
            field: 'ordered_at',
            headerName: 'Data de Pedido',
        },
        {
            field: 'delivery_date',
            headerName: 'Data de Entrega',
        },
        {
            field: 'price',
            headerName: 'Preço',
        },
        {
            field: 'payment_info',
            headerName: 'Pagamento',
        },
        {
            field: 'actions',
            headerName: 'Ações',
        },
    ];

    const filters = [
        { field: 'selected_metal', type: 'select', label: 'Material', options: metalList },
        { field: 'customer', type: 'select', label: 'Cliente', options: customers },
        { field: 'ordered_at_start', type: 'datepicker', label: 'Data de Pedido Início' },
        { field: 'ordered_at_end', type: 'datepicker', label: 'Data de Pedido Fim' },
        { field: 'delivery_date_start', type: 'datepicker', label: 'Data de Entrega Início' },
        { field: 'delivery_date_end', type: 'datepicker', label: 'Data de Entrega Fim' },
        {
            field: 'overdue',
            type: 'select',
            label: 'Prazo',
            options: [
                { value: '1', label: 'Atrasados' },
                { value: '0', label: 'No prazo' },
            ],
        },
    ];

    const componentName = 'financial';

    // Componente de tooltip personalizado para formas de pagamento
    const PaymentTooltip = ({ paymentMethods, totalPrice, children }) => {
        if (!paymentMethods || paymentMethods.length === 0) {
            return children;
        }

        const totalValue = paymentMethods.reduce((sum, method) => sum + (parseFloat(method.price) || 0), 0);

        return (
            <Tooltip
                title={
                    <Card sx={{ p: 2, maxWidth: 300, bgcolor: 'white' }}>
                        <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                            Formas de Pagamento
                        </Typography>
                        <Stack spacing={1}>
                            {paymentMethods.map((method, index) => (
                                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="body2" fontWeight="bold">
                                            {method.payment_name || method.name}
                                        </Typography>
                                        {method.observation && (
                                            <Typography variant="caption" color="text.secondary">
                                                {method.observation}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Typography variant="body2" fontWeight="bold" color="primary">
                                        {formatCurrency(method.price)}
                                    </Typography>
                                </Box>
                            ))}
                            <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 1, mt: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body1" fontWeight="bold">
                                        Total:
                                    </Typography>
                                    <Typography variant="h6" fontWeight="bold" color="success.main">
                                        {formatCurrency(totalValue)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Stack>
                    </Card>
                }
                arrow
                placement="top"
                componentsProps={{
                    tooltip: {
                        sx: {
                            bgcolor: 'transparent',
                            boxShadow: 'none',
                            p: 0,
                        },
                    },
                }}
            >
                {children}
            </Tooltip>
        );
    };

    const renderPaymentInfo = (row) => {
        const hasPaymentMethods = row.payment_methods && row.payment_methods.length > 0;

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PaymentTooltip paymentMethods={row.payment_methods} totalPrice={row.total_price}>
                    <IconButton
                        size="small"
                        color={hasPaymentMethods ? "primary" : "disabled"}
                        title={hasPaymentMethods ? "Ver formas de pagamento" : "Nenhuma forma de pagamento"}
                    >
                        <FaMoneyBillWave size={16} />
                    </IconButton>
                </PaymentTooltip>
            </Box>
        );
    };

    const renderActions = (row) => {
        if (!verifyPermission(componentName, '1')) return null;

        return (
            <Stack spacing={1} direction={'row'} alignItems={'center'} justifyContent={'center'}>
                <Tooltip title="Detalhes" arrow placement="top">
                    <IconButton component={Link} to={`/pedido-detalhes/${row.id}`} color="primary">
                        <FaMagnifyingGlass size={20} />
                    </IconButton>
                </Tooltip>
            </Stack>
        );
    };

    return <ListingPage
        filters={filters}
        filterValues={filterValues}
        onFilterChange={handleFilterChange}
        isLoading={isFetching}
        icon={FaCartShopping}
        pageTitle={"Financeiro"}
        addButtonTitle={"Pedido"}
        addPath={"/novo-pedido"}
        columns={columns}
        data={dataList}
        actions={renderActions}
        paymentInfo={renderPaymentInfo}
    />
};